import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { CLASSES } from "../data/classes";
import { getRank } from "../data/achievements";
import { ELEMENTS } from "../utils/gameLogic";
import { isClassUnlocked, CLASS_UNLOCK_LEVEL } from "../data/profile";
import { ArrowLeft, Swords, Loader2, Lock, X, Crown } from "lucide-react";
import { SFX } from "../utils/audio";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PvP = () => {
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();
  const { profile } = useProfile();
  const [state, setState] = useState("idle"); // idle | queued | matched
  const [match, setMatch] = useState(null);
  const [chosenClass, setChosenClass] = useState(null);
  const [waited, setWaited] = useState(0);
  const pollRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pvp/queue/status`, { withCredentials: true });
      setState(res.data.state);
      if (res.data.match) setMatch(res.data.match);
      if (typeof res.data.waited_seconds === "number") setWaited(res.data.waited_seconds);
      if (res.data.state === "matched" && res.data.match_id) {
        // Stop queue polling and route to the match screen
        stopPoll();
        navigate(`/pvp-duel/${res.data.match_id}`);
      }
      if (res.data.state === "idle" && res.data.reason === "timeout") {
        stopPoll();
        toast.info("Aucun adversaire trouvé. Réessaie quand tu veux !");
      }
    } catch (e) {}
  }, [navigate]);

  useEffect(() => {
    if (user) refresh();
    import("../utils/audio").then(({ playMusic }) => playMusic("pvp"));
    return stopPoll;
  }, [user, refresh]);

  const startPoll = () => {
    stopPoll();
    pollRef.current = setInterval(refresh, 1800);
  };

  const handleJoinQueue = async () => {
    SFX.click();
    try {
      const res = await axios.post(`${API}/pvp/queue/join`, {}, { withCredentials: true });
      setState(res.data.state);
      if (res.data.match) setMatch(res.data.match);
      if (res.data.state === "matched") {
        navigate(`/pvp-duel/${res.data.match_id}`);
        return;
      }
      startPoll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleLeaveQueue = async () => {
    SFX.click();
    stopPoll();
    try {
      await axios.post(`${API}/pvp/queue/leave`, {}, { withCredentials: true });
    } catch (e) {}
    setState("idle");
    setWaited(0);
  };

  const rankInfo = useMemo(() => getRank(profile?.rank_points || 0), [profile?.rank_points]);

  if (!user) {
    return (
      <div className="h-screen w-screen bg-slate-900 bg-dots flex items-center justify-center">
        <div className="text-center max-w-md">
          <Swords className="w-16 h-16 mx-auto text-rose-400 mb-4" />
          <h1 className="font-display text-3xl font-bold mb-3 text-white">PvP Classé</h1>
          <p className="text-slate-300 mb-5">Connecte-toi pour défier d'autres aventuriers et grimper dans le classement.</p>
          <button data-testid="pvp-login-btn" onClick={loginWithGoogle} className="bg-white text-slate-950 border-4 border-slate-950 rounded-2xl px-5 py-3 font-display font-bold cartoon-shadow">
            Se connecter
          </button>
          <div className="mt-4">
            <button onClick={() => navigate("/")} className="text-slate-400 hover:text-white text-sm">← Retour au menu</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-slate-900 bg-dots overflow-y-auto">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <button data-testid="pvp-back" onClick={() => navigate("/")} className="bg-slate-700 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Menu
          </button>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white flex items-center gap-2">
            <Swords className="w-7 h-7 text-rose-400" /> PvP Classé
          </h1>
          <div className="w-20" />
        </div>

        {/* Rank panel */}
        <div className={`bg-gradient-to-r ${rankInfo.current.color} border-4 border-slate-950 rounded-2xl p-3 cartoon-shadow-lg-static mb-5`}>
          <div className={`flex items-center justify-between ${rankInfo.current.text}`}>
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5" />
              <span className="font-display font-bold">Rang : <span data-testid="pvp-rank-label" className="text-base">{rankInfo.current.label}</span></span>
            </div>
            <div className="font-bold text-sm">{profile?.rank_points || 0} pts {rankInfo.next ? `/ ${rankInfo.next.min}` : ""}</div>
          </div>
        </div>

        {/* Queue button or status */}
        {state === "idle" && (
          <div className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-5 cartoon-shadow-lg-static text-center">
            <Swords className="w-12 h-12 text-rose-400 mx-auto mb-3" />
            <p className="text-slate-300 mb-4">Affronte d'autres joueurs en duel tour-par-tour. Tu choisiras ta classe une fois l'adversaire trouvé.</p>
            <p className="text-slate-400 text-xs mb-4">Gains : <span className="text-emerald-400 font-bold">+25 pts</span> si tu gagnes, <span className="text-rose-400 font-bold">-25 pts</span> sinon (min. 0).</p>
            <button
              data-testid="pvp-join-queue"
              onClick={handleJoinQueue}
              className="bg-rose-500 text-white border-4 border-slate-950 rounded-2xl py-4 px-6 font-display font-bold uppercase tracking-wider cartoon-shadow"
            >
              Rechercher un adversaire
            </button>
            <p className="text-[10px] text-slate-500 italic mt-3">
              ⚠️ Si tu n'es pas prêt dans les 5 min suivant la mise en relation, tu perds par forfait.
            </p>
          </div>
        )}

        {state === "queued" && (
          <div data-testid="pvp-queued" className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-5 cartoon-shadow-lg-static text-center">
            <Loader2 className="w-12 h-12 text-rose-400 mx-auto mb-3 animate-spin" />
            <p className="font-display font-bold text-lg">Recherche d'un adversaire…</p>
            <p className="text-slate-400 text-sm">Temps d'attente : <span data-testid="pvp-waited" className="text-yellow-400 font-bold">{waited}s</span></p>
            <p className="text-[10px] text-slate-500 italic mt-2">
              On élargit la recherche progressivement : ≤200 pts (10s) → 400 (30s) → 800 (60s) → infini ensuite. Si rien après 5 min, on annule.
            </p>
            <button data-testid="pvp-leave-queue" onClick={handleLeaveQueue} className="mt-3 bg-slate-700 text-white border-2 border-slate-950 rounded-xl px-3 py-2 text-sm font-bold flex items-center gap-1 mx-auto">
              <X className="w-3 h-3" /> Annuler la recherche
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PvP;
